import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import UserSettingsAccountPassword from './UserSettingsAccountPassword'
import UserSettingsAccountDeactivate from './UserSettingsAccountDeactivate'
import UserSettingsAccountDelete from './UserSettingsAccountDelete'
import { updateOtpLogin } from 'util/userUtils'
import LoadingSkeleton from 'ui/LoadingSkeleton'

const UserSettingsAccount: React.FC = () => {
    const { currentUser, awaitingUser } = useUser()
    const navigate = useNavigate()
    const [openDeactivateModal, setOpenDeactivateModal] = useState<boolean>(false)
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
    const [openPasswordModal, setOpenPasswordModal] = useState<boolean>(false)
    const [otpLogin, setOtpLogin] = useState<boolean>(currentUser?.properties.preferences_auth_otp_login ? currentUser?.properties.preferences_auth_otp_login : false)

    useEffect(() => {
        if (!awaitingUser && !currentUser) {
            navigate('/login', { replace: true })
        }
        setOtpLogin(currentUser?.properties.preferences_auth_otp_login ? currentUser?.properties.preferences_auth_otp_login : false)
    }, [currentUser, awaitingUser, navigate])

    const handleOtpChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const response = await updateOtpLogin(e.target.checked)
            setOtpLogin(response.data.properties.preferences_auth_otp_login)
            if (currentUser) {
                currentUser.properties.preferences_auth_otp_login = response.data.properties.preferences_auth_otp_login
            }
        } catch (error) {
            console.error('Error updating OTP login preference:', error)
        }
    }

    if (!currentUser || awaitingUser) {
        return (
            <LoadingSkeleton count={4} height="h-20" className=""/>
        )
    }

    return (
        <div className="p-2">
            <div className="space-y-2">

                {/* General Section */}
                <div className="form-control space-y-2">
                    <label className="form-label text-lg">General</label>
                    {currentUser?.auth_type === 'local' && (
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        onClick={() => setOpenPasswordModal(true)}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">Password</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">Update your account password.</p>
                        </div>
                        <ChevronRight className="inline-arrow-icon" />
                    </div>
                    )}
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md"
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">OTP login</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">Enable logging in with a one-time passcode.</p>
                        </div>
                            <input
                                type="checkbox"
                                id="otp_enabled"
                                checked={otpLogin}
                                onChange={handleOtpChange}
                                className="toggle-switch-input col-span-1 flex-shrink-0"
                            />
                    </div>
                </div>

                {/* Advanced Section */}
                <div className="form-control space-y-2">
                    <label className="form-label text-lg">Advanced</label>
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        onClick={() => setOpenDeactivateModal(true)}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">Deactivate account</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">Hides your profile from view but leaves your content associated with your username.</p>
                        </div>
                        <ChevronRight className="inline-arrow-icon" />
                    </div>
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        onClick={() => setOpenDeleteModal(true)}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">Delete account</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">Permanently removes your profile and disassociates your username from your content.</p>
                        </div>
                        <ChevronRight className="inline-arrow-icon" />
                    </div>
                </div>
            </div>

            {/* Password Change Modal */}
            <UserSettingsAccountPassword
                isOpen={openPasswordModal}
                onClose={() => setOpenPasswordModal(false)}
            />

            {/* Deactivate Modal */}
            <UserSettingsAccountDeactivate
                isOpen={openDeactivateModal}
                onClose={() => setOpenDeactivateModal(false)}
            />

            {/* Delete Modal */}
            <UserSettingsAccountDelete
                isOpen={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
            />
        </div>
    )
}

export default UserSettingsAccount
